/**
 * Données « carte à collectionner » du verso d'une SKILLCARD (PUR, testé) :
 * plage de niveaux min → max, élément emblématique (formule / résumé), XP max
 * formatée et taille du titre selon sa longueur.
 */

import { SCHOOL_LEVELS } from '@jeraisonne/db';
import type { CardContent } from '@jeraisonne/db/cards';

/** « 4ème → 2nde », ou « 3ème » pour un seul niveau ; '' si aucun (ordre scolaire). */
export function cardLevelRange(levels: readonly string[]): string {
  const order = SCHOOL_LEVELS.map((l) => l.value as string);
  const idx = levels.map((l) => order.indexOf(l)).filter((i) => i >= 0);
  if (idx.length === 0) return '';
  const min = Math.min(...idx);
  const max = Math.max(...idx);
  const labelOf = (i: number) => SCHOOL_LEVELS[i]?.label ?? '';
  return min === max ? labelOf(min) : `${labelOf(min)} → ${labelOf(max)}`;
}

export type CardEmblem = { kind: 'formula'; latex: string } | { kind: 'text'; md: string };

export const CARD_EMBLEM_MAX = 110;

/** Coupe au dernier mot avant `max`, sans jamais laisser un `$…$` ouvert. */
export function truncateEmblem(text: string, max = CARD_EMBLEM_MAX): string {
  // Espaces normales réduites ; les insécables (avant « : », dans « 1 000 », « 3 m ») sont gardées.
  const clean = text.replace(/[^\S\u00a0\u202f]+/g, ' ').trim();
  if (clean.length <= max) return clean;
  let cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > max * 0.6) cut = cut.slice(0, lastSpace);
  // Nombre impair de `$` → formule coupée : on s'arrête avant elle.
  if ((cut.match(/\$/g) ?? []).length % 2 === 1) cut = cut.slice(0, cut.lastIndexOf('$')).trimEnd();
  return `${cut.replace(/[\s,;:.–-]+$/, '')}…`;
}

/** Première phrase d'un texte (markdown simple : puces et gras retirés). */
function firstSentence(text: string): string {
  const plain = text
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/\*\*/g, '')
    .replace(/^#+\s*/gm, '')
    .trim();
  const m = plain.match(/^[\s\S]*?[.!?](?=\s|$)/);
  return (m ? m[0] : plain).trim();
}

/**
 * L'élément EMBLÉMATIQUE affiché sous le titre du verso : la formule de la
 * carte si elle en a une (« AB² = AC² + BC² »), sinon un résumé court tiré du
 * recto (texte, puis 1re étape), sinon de la règle / méthode saisie. `null` si
 * rien d'exploitable.
 */
export function cardEmblem(
  content: CardContent | null | undefined,
  fallbackText: string,
): CardEmblem | null {
  const sections = content?.sections ?? [];
  const formula = sections.find((s) => s.kind === 'formula');
  if (formula && formula.kind === 'formula' && formula.latex.trim()) {
    return { kind: 'formula', latex: formula.latex.trim() };
  }
  for (const s of sections) {
    if (s.kind === 'markdown' && s.md.trim()) {
      return { kind: 'text', md: truncateEmblem(firstSentence(s.md)) };
    }
  }
  const steps = sections.find((s) => s.kind === 'steps');
  if (steps && steps.kind === 'steps' && steps.items[0]?.trim()) {
    return { kind: 'text', md: truncateEmblem(steps.items[0]) };
  }
  const fb = firstSentence(fallbackText);
  return fb ? { kind: 'text', md: truncateEmblem(fb) } : null;
}

/** « 1 000 », « 2 500 » — texte (étiquette accessible), espace insécable dès 4 chiffres. */
export function formatCardXp(xp: number): string {
  return cardXpGroups(xp).join('\u00A0');
}

/**
 * Groupes de 3 chiffres (« 1 », « 000 »). Le badge les affiche séparément avec
 * un écart CSS : la police Outfit du badge rend les espaces insécables SANS
 * largeur (« 1000 » collé), et `Intl.NumberFormat('fr-FR')` ne groupe pas les
 * nombres à 4 chiffres selon le navigateur.
 */
export function cardXpGroups(xp: number): string[] {
  const digits = String(Math.max(0, Math.round(xp)));
  const groups: string[] = [];
  for (let end = digits.length; end > 0; end -= 3) {
    groups.unshift(digits.slice(Math.max(0, end - 3), end));
  }
  return groups;
}

/**
 * Étiquette du verso : « #Schéma de raisonnement » — dièse, minuscules,
 * majuscule initiale (le reste est mis en minuscules, accents conservés).
 */
export function cardHashtag(label: string): string {
  const clean = label
    .trim()
    .replace(/^#+\s*/, '')
    .toLocaleLowerCase('fr');
  return clean ? `#${clean.charAt(0).toLocaleUpperCase('fr')}${clean.slice(1)}` : '';
}

/** Titre du verso : grand s'il est court, réduit s'il est long (reste sur 3 lignes). */
export function versoTitleSize(title: string): 'lg' | 'md' | 'sm' {
  const n = title.trim().length;
  if (n <= 32) return 'lg';
  if (n <= 60) return 'md';
  return 'sm';
}
